"use client";

import { useState, useEffect, useRef, useCallback } from "react";

interface UseScrollVisibilityOptions {
  threshold?: number;
  showDelay?: number;
}

export function useScrollVisibility({
  threshold = 20,
  showDelay = 800,
}: UseScrollVisibilityOptions = {}) {
  const [visible, setVisible] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    if (currentScrollY <= 10) {
      setVisible(true);
      lastScrollY.current = currentScrollY;
      return;
    }

    const diff = currentScrollY - lastScrollY.current;

    if (diff > threshold) {
      setVisible(false);
      setIsScrolling(true);
    } else if (diff < -threshold) {
      setVisible(true);
      setIsScrolling(true);
    }

    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    scrollTimeout.current = setTimeout(() => {
      setVisible(true);
      setIsScrolling(false);
    }, showDelay);

    lastScrollY.current = currentScrollY;
  }, [threshold, showDelay]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [handleScroll]);

  return { visible, isScrolling };
}
